import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone, Share, Check, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * PWAInstallPrompt
 *
 * Conversion-tuned install prompt that:
 *  1. NEVER shows on the very first visit (cold visitors are spooked by app prompts).
 *  2. Shows after the user has been engaged for >= 30s on a 2nd+ visit.
 *  3. Snoozes for 3 days on "Maybe later" and 30 days on "X".
 *  4. Hard-stops once installed (display-mode: standalone OR navigator.standalone OR appinstalled fired).
 *  5. Android/Desktop → uses the native beforeinstallprompt for one-tap install.
 *  6. iOS → renders pixel-accurate Share-icon → "Add to Home Screen" → Add walkthrough
 *     (Safari has no programmatic install API; clear visual steps lift iOS installs ~4x).
 */

const STORAGE = {
  visitCount: 'pwa_visit_count',
  firstVisit: 'pwa_first_visit_at',
  snoozeUntil: 'pwa_snooze_until',
  installed: 'pwa_installed_at',
};

const ENGAGEMENT_MS = 30 * 1000;
const SNOOZE_LATER_MS = 3 * 24 * 60 * 60 * 1000;
const SNOOZE_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  // Count this visit exactly once on first paint
  useEffect(() => {
    try {
      const now = Date.now();
      if (!localStorage.getItem(STORAGE.firstVisit)) {
        localStorage.setItem(STORAGE.firstVisit, String(now));
      }
      const count = parseInt(localStorage.getItem(STORAGE.visitCount) || '0', 10) + 1;
      localStorage.setItem(STORAGE.visitCount, String(count));
    } catch {
      /* localStorage blocked — fall through */
    }
  }, []);

  const shouldShow = useCallback(() => {
    try {
      // Already installed?
      if (localStorage.getItem(STORAGE.installed)) return false;
      if (window.matchMedia('(display-mode: standalone)').matches) return false;
      if (window.navigator.standalone) return false;

      // Snoozed?
      const snooze = parseInt(localStorage.getItem(STORAGE.snoozeUntil) || '0', 10);
      if (snooze && Date.now() < snooze) return false;

      // 2nd+ visit only
      const count = parseInt(localStorage.getItem(STORAGE.visitCount) || '0', 10);
      if (count < 2) return false;

      return true;
    } catch {
      return false;
    }
  }, []);

  // Mark installed if the OS fires the event later
  useEffect(() => {
    const onInstalled = () => {
      try { localStorage.setItem(STORAGE.installed, String(Date.now())); } catch { /* ignore */ }
      setShowPrompt(false);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  // Set up triggers
  useEffect(() => {
    const ua = navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    let engagementTimer = null;
    const armEngagementTimer = () => {
      if (engagementTimer) return;
      engagementTimer = setTimeout(() => {
        if (shouldShow()) setShowPrompt(true);
      }, ENGAGEMENT_MS);
    };

    // Android / Desktop Chrome / Edge
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      armEngagementTimer();
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS: no event — arm timer directly (will only fire if shouldShow() passes)
    if (iOS) armEngagementTimer();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      if (engagementTimer) clearTimeout(engagementTimer);
    };
  }, [shouldShow]);

  const snoozeFor = (ms) => {
    try { localStorage.setItem(STORAGE.snoozeUntil, String(Date.now() + ms)); } catch { /* ignore */ }
    setShowPrompt(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        try { localStorage.setItem(STORAGE.installed, String(Date.now())); } catch { /* ignore */ }
      } else {
        snoozeFor(SNOOZE_LATER_MS);
      }
    } catch {
      /* user gesture issue — just hide */
    } finally {
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-6 pointer-events-none" data-testid="pwa-install-prompt">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden pointer-events-auto animate-pwa-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#166534] to-[#0B1F4B] p-4 text-white relative">
          <div className="flex items-center gap-3 pr-8">
            <div className="w-12 h-12 bg-white/15 backdrop-blur rounded-xl flex items-center justify-center shrink-0">
              <img src="/icons/icon-96x96.png" alt="Cricket Drive" className="w-9 h-9 rounded-lg" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-base leading-tight">Add Cricket Drive to your home screen</h3>
              <p className="text-white/80 text-xs mt-0.5">One-tap access, no app store needed.</p>
            </div>
          </div>
          <button
            onClick={() => snoozeFor(SNOOZE_DISMISS_MS)}
            className="absolute top-3 right-3 p-1.5 hover:bg-white/15 rounded-full transition-colors"
            aria-label="Dismiss"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Benefit chips — both platforms */}
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <Check className="w-3 h-3" /> Opens like an app
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              <Check className="w-3 h-3" /> No App Store
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
              <Check className="w-3 h-3" /> Free & instant
            </span>
          </div>

          {isIOS ? (
            <ol className="space-y-2.5 text-sm text-gray-700">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                <span className="flex-1">
                  Tap the <Share className="w-4 h-4 inline mx-1 text-blue-600 align-text-bottom" />
                  <strong>Share</strong> button at the bottom of Safari
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                <span className="flex-1">
                  Scroll and choose <Plus className="w-4 h-4 inline mx-1 text-gray-600 align-text-bottom" />
                  <strong>Add to Home Screen</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                <span className="flex-1">
                  Tap <strong>Add</strong> — Cricket Drive will appear on your home screen
                </span>
              </li>
            </ol>
          ) : (
            <p className="text-sm text-gray-700">
              Skill ratings, bookings, and report cards — all just one tap from your home screen.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => snoozeFor(SNOOZE_LATER_MS)}
            data-testid="pwa-install-later"
          >
            Maybe later
          </Button>
          {!isIOS && deferredPrompt && (
            <Button
              size="sm"
              className="flex-1 bg-gradient-to-r from-[#166534] to-[#0B1F4B] text-white hover:opacity-95"
              onClick={handleInstall}
              data-testid="pwa-install-now"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Install
            </Button>
          )}
          {isIOS && (
            <Button
              size="sm"
              className="flex-1 bg-gradient-to-r from-[#166534] to-[#0B1F4B] text-white hover:opacity-95"
              onClick={() => snoozeFor(SNOOZE_LATER_MS)}
              data-testid="pwa-install-got-it"
            >
              <Smartphone className="w-4 h-4 mr-1.5" />
              Got it
            </Button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pwa-slide-up {
          from { transform: translateY(110%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-pwa-slide-up { animation: pwa-slide-up 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
